import { useRef, useState } from "react";
import {
  Alert, Autocomplete, Box, Button, Checkbox, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, LinearProgress, Stack, TextField, Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { planRegistrationCopy } from "./registrationCopy";

const emptySelection = () => ({ sourceId: "", itemIds: [], targetIds: [] });
const wrapping = { overflowWrap: "anywhere", minWidth: 0, whiteSpace: "normal" };

export default function BulkCopyButton({ kind, label, loadSnapshot, saveCopy, onCopied, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [snapshot, setSnapshot] = useState([]);
  const [selection, setSelection] = useState(emptySelection);
  const [phase, setPhase] = useState("select");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const pending = useRef(false);
  const source = snapshot.find((row) => row.id === selection.sourceId);
  let plan;
  try { plan = planRegistrationCopy(kind, snapshot, selection); } catch { /* Incomplete selection. */ }

  const load = async () => {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setError("");
    try { setSnapshot(await loadSnapshot()); }
    catch { setError("데이터를 불러오지 못했습니다. 다시 시도하세요."); }
    finally { pending.current = false; setBusy(false); }
  };
  const start = () => {
    if (pending.current) return;
    setSnapshot([]);
    setSelection(emptySelection());
    setPhase("select");
    setResult(null);
    setOpen(true);
    load();
  };
  const change = (next) => { setSelection(next); setError(""); };
  const confirm = async () => {
    if (pending.current || !plan) return;
    pending.current = true;
    setBusy(true);
    setError("");
    let completed;
    try {
      const fresh = await loadSnapshot();
      setSnapshot(fresh);
      const current = planRegistrationCopy(kind, fresh, selection);
      // Never silently expand a preview when another user changes the links.
      if (JSON.stringify(current.payload) !== JSON.stringify(plan.payload)) {
        setError("연결 상태가 변경되었습니다. 갱신된 추가·제외 건수를 확인한 뒤 확정하세요.");
        return;
      }
      if (current.added) await saveCopy(current.payload);
      completed = current;
      setResult(current);
      setPhase("done");
    } catch (err) {
      setError(err?.message || "복사에 실패했습니다. 다시 시도하면 연결 상태를 재확인합니다.");
    } finally {
      pending.current = false;
      setBusy(false);
    }
    if (completed) {
      try { await onCopied?.(); }
      catch { setError("복사는 완료되었으나 목록을 갱신하지 못했습니다. 목록을 새로고침하세요."); }
    }
  };

  return <>
    <Button variant="outlined" startIcon={<ContentCopyIcon />} disabled={disabled} onClick={start}>{label}</Button>
    <Dialog open={open} onClose={() => !pending.current && setOpen(false)} fullWidth maxWidth="md"
      aria-labelledby={`bulk-copy-${kind}-title`}
      PaperProps={{ sx: { m: { xs: 1, sm: 3 }, width: { xs: "calc(100% - 16px)", sm: "100%" }, maxHeight: "calc(100dvh - 32px)" } }}>
      <DialogTitle id={`bulk-copy-${kind}-title`}>{label}</DialogTitle>
      {busy && <LinearProgress aria-label="복사 처리 중" />}
      <DialogContent dividers sx={{ minWidth: 0, p: { xs: 2, sm: 3 } }}>
        <Stack spacing={2}>
          {error && <Alert severity="error" sx={wrapping}>{error}</Alert>}
          {phase === "done" ? <Alert severity="success">
            연결 완료: {result.added}건 · 기존 연결 제외: {result.skipped}건
          </Alert> : <>
            <Alert severity="info">기존 연결은 유지됩니다. 이미 연결된 항목은 제외됩니다.</Alert>
            {phase === "select" ? <>
              <Autocomplete options={snapshot.filter((row) => row.items.length)} value={source || null}
                disabled={busy} getOptionLabel={(row) => row.label} isOptionEqualToValue={(a, b) => a.id === b.id}
                noOptionsText="복사할 연결이 없습니다"
                onChange={(_, row) => change({ sourceId: row?.id || "", itemIds: [], targetIds: [] })}
                renderInput={(params) => <TextField {...params} label={kind === "machine" ? "원본 설비" : "원본 설비·공정"} />}
                renderOption={(props, row) => <li {...props} key={row.id} style={wrapping}>{row.label}</li>} />
              {source && <Box component="fieldset" sx={{ m: 0, px: 1.5, py: 1, minWidth: 0, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                <Typography component="legend" variant="body2">복사할 {kind === "machine" ? "공정" : "유해·위험요인"} ({selection.itemIds.length}건)</Typography>
                <FormControlLabel label="항목 전체 선택" control={<Checkbox disabled={busy}
                  checked={selection.itemIds.length === source.items.length}
                  indeterminate={selection.itemIds.length > 0 && selection.itemIds.length < source.items.length}
                  onChange={(_, checked) => change({ ...selection, itemIds: checked ? source.items.map((item) => item.id) : [] })} />} />
                <Stack sx={{ maxHeight: 240, overflowY: "auto", overflowX: "hidden" }}>
                  {source.items.map((item) => <FormControlLabel key={item.id} sx={{ alignItems: "flex-start", "& .MuiFormControlLabel-label": { ...wrapping, pt: 1 } }}
                    label={item.label} control={<Checkbox disabled={busy} checked={selection.itemIds.includes(item.id)}
                      onChange={(_, checked) => change({ ...selection, itemIds: checked
                        ? [...selection.itemIds, item.id] : selection.itemIds.filter((id) => id !== item.id) })} />} />)}
                </Stack>
              </Box>}
              <Autocomplete multiple disableCloseOnSelect disabled={busy || !source}
                options={snapshot.filter((row) => row.id !== selection.sourceId)}
                value={snapshot.filter((row) => selection.targetIds.includes(row.id))}
                getOptionLabel={(row) => row.label} isOptionEqualToValue={(a, b) => a.id === b.id}
                noOptionsText="복사 대상이 없습니다"
                onChange={(_, rows) => change({ ...selection, targetIds: rows.map((row) => row.id) })}
                renderInput={(params) => <TextField {...params} label={kind === "machine" ? "대상 설비" : "대상 설비·공정"} />}
                renderOption={(props, row, { selected }) => <li {...props} key={row.id}>
                  <Checkbox checked={selected} tabIndex={-1} sx={{ flexShrink: 0 }} /><Box sx={wrapping}>{row.label}</Box>
                </li>}
                sx={{ "& .MuiChip-root": { maxWidth: "100%", height: "auto" }, "& .MuiChip-label": { ...wrapping, py: 0.75 } }} />
              {!snapshot.length && !busy && <Button onClick={load}>다시 불러오기</Button>}
            </> : <>
              <Typography sx={wrapping}>원본: {source?.label || "선택한 원본 없음"}</Typography>
              <Typography variant="body2" sx={wrapping}>{plan?.items.map((item) => item.label).join(" · ")}</Typography>
              {plan?.rows.map((row) => <Box key={row.target.id} sx={{ py: 1, borderBottom: "1px solid", borderColor: "divider" }}>
                <Typography sx={wrapping}>{row.target.label}</Typography>
                <Typography variant="body2">추가 {row.items.length}건 · 기존 연결 제외 {row.skipped}건</Typography>
              </Box>)}
              <Typography fontWeight={600}>합계: 추가 {plan?.added || 0}건 · 기존 연결 제외 {plan?.skipped || 0}건</Typography>
            </>}
          </>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ flexWrap: "wrap", gap: 1, p: 2 }}>
        <Button disabled={busy} onClick={() => setOpen(false)}>{phase === "done" ? "닫기" : "취소"}</Button>
        {phase === "preview" && <Button disabled={busy} onClick={() => { setPhase("select"); setError(""); }}>선택 수정</Button>}
        {phase === "select" && <Button variant="contained" disabled={busy || !plan} onClick={() => setPhase("preview")}>복사 미리보기</Button>}
        {phase === "preview" && <Button variant="contained" startIcon={<ContentCopyIcon />} disabled={busy || !plan}
          onClick={confirm}>{plan?.added ? `${plan.added}건 복사 확정` : "기존 연결 확인"}</Button>}
      </DialogActions>
    </Dialog>
  </>;
}
